<template>
  <div class="buy-product" :class="rootClasses">
    <product-price
      class="buy-product__prices"
      v-if="!buttonOnly"
      different-sizes
      :product-id="productId"
      :default-currency="defaultCurrency"
      :is-from-price="productVariations?.length > 0" />
    <button class="buy-product__action" :disabled="cartStore.isRemoving" @click.stop="addToCart">
      <icon-spinner class="buy-product__spinner" v-if="addingToCart" />
      <icon-check v-else-if="itemInCart" />
      <icon-shopping-cart v-else />

      <span v-if="buttonLarge">
        {{ buyButtonText }}
      </span>
    </button>
    <custom-dialog
      v-if="selectionOpen"
      class="buy-product__overlay"
      @dialog-closed="closeSelection"
      :dialog-title="`Select Variation for ${productName}`">
      <variation-selection
        :product-variations="productVariations"
        :product-subscription-options="productSubscriptionOptions"
        :product-details="productDetails"
        @on-selection="closeSelection" />
    </custom-dialog>
  </div>
</template>

<script lang="ts">
import { useCartStore } from "~/stores/cart";
import { defineAsyncComponent, defineComponent } from '#imports';

const CustomDialog = defineAsyncComponent(() => import('~/components/parts/CustomDialog.vue'));
const VariationSelection = defineAsyncComponent(() => import('~/components/parts/product/VariationSelection.vue'));
const IconCheck = defineAsyncComponent(() => import('~/assets/icons/IconCheck.svg'));
const IconSpinner = defineAsyncComponent(() => import('~/assets/icons/IconSpinner.svg'));
const IconShoppingCart = defineAsyncComponent(() => import('~/assets/icons/IconShoppingCart.svg'));

export default defineComponent({
  setup() {
    return {
      cartStore: useCartStore(),
    }
  },
  props: {
    productId: [Number, String],
    defaultCurrency: {
      type: Object,
      required: true,
    },
    buttonStyle: String,
    productName: String,
    productVariations: {
      type: Object,
      required: false,
    },
    productSubscriptionOptions: Array,
    productDetails: [Array, null],
    isSubscription: Boolean,
    variationSelectorAvailable: Boolean,
  },
  components: {
    IconShoppingCart,
    CustomDialog,
    VariationSelection,
    IconCheck,
    IconSpinner,
  },
  data () {
    return {
      selectionOpen: false,
    };
  },
  computed: {
    showVariationSelector() {
      if (! this.variationSelectorAvailable) {
        // Do not offer variation selector when buy product used on product page
        return false;
      }

      return this.productSubscriptionOptions?.schemeKey || this.productVariations
    },
    addingToCart () {
      return this.cartStore.addingToCart.includes(this.productId);
    },
    buttonOnly (): boolean {
      return this.buttonStyle?.includes('button-only');
    },
    buttonLarge (): boolean {
      return this.buttonStyle?.includes('button-large');
    },
    rootClasses (): Record<string, boolean> {
      return {
        'buy-product--button-only': this.buttonOnly,
        'buy-product--button-large': this.buttonLarge,
        'buy-product--in-cart': this.itemInCart,
      };
    },
    itemInCart (): boolean {
      return this.cartStore.getCartProductIds.includes(parseInt(this.productId, 10));
    },
    buyButtonText (): string {
      if (this.addingToCart) {
        return 'Adding to cart...';
      }

      if (this.itemInCart) {
        return 'Added to cart';
      }

      return this.showVariationSelector
        ? 'Select Variation'
        : 'Add To Cart';
    },
  },
  methods: {
    async addToCart() {
      if (this.showVariationSelector) {
        this.selectionOpen = true;
      } else {
        let subscriptionScheme = this.isSubscription ? this.productSubscriptionOptions?.schemeKey : '0';
        await this.cartStore.addToCart(this.productId,  1, subscriptionScheme);
      }
    },
    closeSelection () {
      this.selectionOpen = false;
    },
  },
});
</script>

<style lang="scss">
@use "~/common/scss/button";
@use "~/common/scss/helpers";
@use "~/common/scss/mq";

.buy-product {
  $root: &;

  align-self: flex-start;
  align-items: center;
  display: flex;
  gap: .25rem;
  height: auto;

  &--button-only {
    width: 100%;

    #{$root}__action {
      width: 100%;
    }
  }

  &__prices {
    flex-flow: column;
  }

  &__action {
    @include button.buttonOrange();
    @include button.buttonMedium();

    align-items: center;
    box-sizing: border-box;
    display: flex;
    gap: .25rem;

    #{$root}--button-large & {
      @include mq.aboveTablet() {
        @include button.buttonMediumLarge();
      }
      @include mq.tabletOnly() {
        @include button.buttonMedium();
      }
      @include mq.mobileOnly() {
        @include button.buttonSmall();
      }
    }

    #{$root}--in-cart & {
      
      @include button.buttonGreen();
    }
  }

  &__spinner {
    @include button.spinner();
  }

  &__overlay {
    --overlay-width: #{helpers.px-to-rem(340)};
  }
}
</style>
